/**
 * BasketModal - Compact in-account checkout for meal changes
 */

import React, { useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";

import { QuizButton } from "../shared/QuizButton";
import { PawsLoader } from "../shared/PawsLoader";
import type { BasketItem } from "../../context/AccountContext";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  open: boolean;
  onClose: () => void;
  basket: BasketItem[];
  onRemoveItem: (variantId: string) => void;
  onClearBasket: () => void;
  onCheckout: () => Promise<void>;
};

export function BasketModal({
  open,
  onClose,
  basket,
  onRemoveItem,
  onClearBasket,
  onCheckout,
}: Props) {
  const [checkoutState, setCheckoutState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const isLoading = checkoutState === "loading";
  const [errorMessage, setErrorMessage] = useState("");

  const totalItems = basket.reduce((sum, item) => sum + Math.abs(item.quantity), 0);
  const totalPrice = basket.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const additions = basket.filter((item) => item.quantity > 0);
  const removals = basket.filter((item) => item.quantity < 0);

  const handleCheckout = async () => {
    setCheckoutState("loading");
    setErrorMessage("");

    try {
      await onCheckout();
      setCheckoutState("success");
      // Auto-close after 2 seconds
      setTimeout(() => {
        setCheckoutState("idle");
        onClose();
      }, 2000);
    } catch (error: any) {
      setCheckoutState("error");
      setErrorMessage(error?.message || "Checkout failed. Please try again.");
    }
  };

  const handleClose = () => {
    if (checkoutState !== "loading") {
      setCheckoutState("idle");
      setErrorMessage("");
      onClose();
    }
  };

  // Success screen
  if (checkoutState === "success") {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px",
              p: 4,
              textAlign: "center",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: "#2E7D32" }} />
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: QUIZ.color.brown }}>
            Success!
          </Typography>
          <Typography sx={{ fontSize: 15, color: QUIZ.color.brown, opacity: 0.75 }}>
            Your subscription has been updated
          </Typography>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: "16px" },
            maxHeight: { xs: "100%", sm: "90vh" },
            height: { xs: "100%", sm: "auto" },
            m: { xs: 0, sm: 2 },
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* Header - Fixed */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #FAF5EB 0%, #FFF 100%)",
          borderBottom: "1px solid rgba(0,0,0,.08)",
          p: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: QUIZ.color.brown }}>
              Review Changes
            </Typography>
            <Typography sx={{ fontSize: 13, color: QUIZ.color.brown, opacity: 0.65, mt: 0.25 }}>
              {totalItems} item{totalItems !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={isLoading} sx={{ color: QUIZ.color.brown }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {/* Loading State */}
        {isLoading && (
          <PawsLoader
            action="saving"
            fullScreen={false}
            size="small"
          />
        )}

        {!isLoading && basket.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: QUIZ.color.brown, mb: 1 }}>
              Your basket is empty
            </Typography>
            <Typography sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.65 }}>
              Edit meals to add items
            </Typography>
          </Box>
        )}

        {!isLoading && basket.length > 0 && (
          <>
            {/* Compact meal list */}
            <Stack spacing={1}>
              {basket.map((item) => {
                const isAdding = item.quantity > 0;
                return (
                  <Box
                    key={item.variantId}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "40px 1fr auto auto",
                      gap: 1,
                      alignItems: "center",
                      border: `1px solid ${isAdding ? "#E5FFEA" : "#FFE5E2"}`,
                      borderRadius: "10px",
                      p: 1,
                      background: isAdding ? "#F7FFF5" : "#FFF5F4",
                    }}
                  >
                    {/* Image */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        overflow: "hidden",
                        background: QUIZ.color.cream,
                        border: "1px solid rgba(0,0,0,.08)",
                      }}
                    >
                      {item.imageUrl && (
                        <Box
                          component="img"
                          src={item.imageUrl}
                          alt={item.productTitle}
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )}
                    </Box>

                    {/* Info */}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: QUIZ.color.brown,
                          lineHeight: 1.2,
                        }}
                        noWrap
                      >
                        {item.productTitle}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: QUIZ.color.brown, opacity: 0.6 }}>
                        {item.variantTitle}
                      </Typography>
                    </Box>

                    {/* Quantity badge */}
                    <Chip
                      label={`${isAdding ? "+" : ""}${item.quantity}`}
                      size="small"
                      sx={{
                        background: isAdding ? "#E5FFEA" : "#FFE5E2",
                        color: isAdding ? "#2E7D32" : "#D14C3F",
                        fontWeight: 800,
                        height: 24,
                        fontSize: 11,
                      }}
                    />

                    {/* Remove */}
                    <IconButton
                      size="small"
                      onClick={() => onRemoveItem(item.variantId)}
                      disabled={isLoading}
                      sx={{ width: 28, height: 28, color: "#D14C3F" }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>

            {/* Error message */}
            {checkoutState === "error" && errorMessage && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  background: "#FFE5E2",
                  border: "1px solid #D14C3F",
                  borderRadius: "10px",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#D14C3F", fontWeight: 600 }}>
                  {errorMessage}
                </Typography>
              </Box>
            )}

            {/* Price Impact */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                background: totalPrice >= 0 ? "#FFF0D6" : "#E5FFEA",
                borderRadius: "12px",
                border: `2px solid ${totalPrice >= 0 ? "#CE7A00" : "#2E7D32"}`,
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: QUIZ.color.brown, mb: 1 }}>
                Price Impact
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                  {totalPrice >= 0 ? "Additional cost:" : "You'll save:"}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 900, color: totalPrice >= 0 ? "#CE7A00" : "#2E7D32" }}>
                  {totalPrice >= 0 ? "+" : ""}£{Math.abs(totalPrice).toFixed(2)}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: QUIZ.color.brown, opacity: 0.65, mt: 0.5 }}>
                per delivery
              </Typography>
            </Box>

          </>
        )}
      </Box>

      {/* Sticky Footer */}
      {basket.length > 0 && checkoutState !== "loading" && (
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          <Stack spacing={1.5}>
            <QuizButton
              variant="primary"
              onClick={handleCheckout}
              fullWidth
            >
              Confirm & Update
            </QuizButton>
            <QuizButton
              variant="secondary"
              onClick={onClearBasket}
              fullWidth
            >
              Clear All
            </QuizButton>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
